import { ObservableQuery } from '@apollo/client'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  BreadcrumbJsonLd,
  CarouselJsonLd,
  CollectionPageJsonLd,
  NextSeo
} from 'next-seo'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import { PILLAR_PAGE_MAP, PillarPage } from 'src/constants/searchPages'
import { createStructuredData } from 'src/constants/structuredData'
import { PostOrderByInput } from 'src/types/hygraph'
import { isANumber, isNonNegative } from 'src/utils'
import { processHygraphRequestError } from 'src/utils/errors-hygraph'
import {
  BLOG_TOPICS,
  HygraphTopics,
  IBlogPillar,
  PILLAR_BLOG_TOPIC,
  QueryCombinedResult,
  TOPIC_MAP,
  getPostTopic,
  getPosts
} from 'src/utils/hygraph'
import { IPost } from '../../src/types/hygraph'
import { RedirectUrls } from '../../src/types/types'

import BlogManager from 'src/components/BlogManager'
import Footer from 'src/components/Footer'
import NavBar from 'src/components/LandingNav'
import {
  HYGRAPH_PAGE_REVALIDATE_SECONDS,
  getPostDates,
  withCleanTitle
} from 'src/utils/postSeo'
import { getCanonicalUrl, toOgLocale } from 'src/utils/seo'

const RevalidateButton = dynamic(
  () => import('src/components/BlogManager/RevalidateButton')
)
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

export const BLOG_PAGE_SIZE = 7

// Page 2+ must not duplicate page 1's title/description.
const withPageSuffix = (
  meta: { title: string; description: string },
  page: number | null
) =>
  page && page >= 2
    ? {
        title: `${meta.title} — Página ${page}`,
        description: `${meta.description} — Página ${page}`
      }
    : meta

const getBlogMeta = (pillar: PillarPage | 'anfitrion' | null) => {
  const baseTitle = 'SpotMe Blog'

  if (!pillar) {
    return {
      title: `${baseTitle} - Consejos sobre almacenamiento y organización`,
      description:
        'Consejos sobre rentar estacionamientos, mini-bodegas y organizar tu espacio. Guías y recursos para ofrecer o rentar de forma segura en SpotMe.'
    }
  }

  if (pillar === 'anfitrion') {
    return {
      title: `${baseTitle} - Para Anfitriones`,
      description:
        'Consejos para anfitriones sobre cómo rentar espacios y generar ingresos extra. Aprende a optimizar tu espacio.'
    }
  }

  // Use PILLAR_PAGE_MAP for the other 4 pillar pages
  const pillarData = PILLAR_PAGE_MAP[pillar as PillarPage]
  return {
    title: `${baseTitle} - ${pillarData.label}`,
    description: `Guías y consejos sobre ${pillarData.searchName.toLowerCase()} en México: cómo elegir, comparar precios y ahorrar. Recursos actualizados de SpotMe.`
  }
}

const image = 'https://www.spotme.mx/images/cover.png'
const getBlogUrl = (basePath: string, page: number | null) =>
  `${basePath}${page ? `/page/${page}` : ''}`

type IProps = {
  firstPost: IPost | null
  posts: IPost[]
  totalPosts: number
  page: number | null
  locale: string
  topic: HygraphTopics | null
  pillar: PillarPage | 'anfitrion' | null
}

const Page: NextPage<IProps> = ({
  firstPost,
  posts,
  totalPosts,
  page,
  locale,
  topic,
  pillar
}) => {
  const { t } = useTranslation('common')
  const basePath = pillar ? `/${pillar}/blog` : RedirectUrls.Blog
  const url = getCanonicalUrl(getBlogUrl(basePath, page), locale)
  const { title, description } = withPageSuffix(getBlogMeta(pillar), page)
  const structuredDate = createStructuredData(
    { '@type': 'Blog', name: title, description, url },
    { provider: { '@id': 'https://www.spotme.mx' } }
  )

  return (
    <>
      <NextSeo
        title={title}
        description={description ?? ''}
        canonical={url}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [{ url: image, width: 1200, height: 630 }]
        }}
      />
      <Script
        id="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredDate) }}
      />
      <CollectionPageJsonLd
        name="Blog"
        hasPart={posts.map((post) => ({
          about: post.title,
          author: { '@type': 'Person', givenName: post.author?.name },
          name: post.title,
          datePublished: getPostDates(post).datePublished,
          audience: { '@type': 'Audience', audienceType: 'Internet' },
          thumbnailUrl: post.coverImage?.url,
          image: post.coverImage?.url
        }))}
      />
      <CarouselJsonLd
        ofType="default"
        data={posts.map((post) => {
          const { basePath } = getPostTopic(post)
          return { url: `https://www.spotme.mx${basePath}/${post.slug}` }
        })}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'SpotMe', item: 'https://www.spotme.mx' },
          {
            position: 2,
            name: pillar
              ? (PILLAR_PAGE_MAP[pillar as PillarPage]?.label ?? 'Blog')
              : 'Blog',
            item: `https://www.spotme.mx${basePath}`
          }
        ]}
      />

      <main className="bg-white-500">
        <NavBar />

        <BlogManager
          firstPost={firstPost}
          posts={posts}
          totalPosts={totalPosts}
          page={page ?? 1}
          pageSize={BLOG_PAGE_SIZE}
          categories={BLOG_TOPICS.map((topic) => {
            const { label, pillar } = TOPIC_MAP[topic]
            return {
              id: topic,
              name: label,
              path: `/${pillar}/blog`
            }
          })}
          selectedCategory={topic}
          basePath={pillar ? `/${pillar}/blog` : RedirectUrls.Blog}
        />

        <RevalidateButton />

        <Footer />
        <WhatsAppButton msg={t('WHATSAPP_BLOG_MSG')} />
      </main>
    </>
  )
}
export default Page

type IParams = { page?: string; pillar?: IBlogPillar }

export const createGetStaticProps =
  (defaultPillar?: IBlogPillar): GetStaticProps<IProps> =>
  async (context) => {
    const { locale = 'es', params } = context

    const { page: p, pillar: pillarParam } = (params ?? {
      page: null,
      pillar: defaultPillar ?? null
    }) as IParams
    const pillar = pillarParam ?? defaultPillar ?? null
    const topic = pillar ? PILLAR_BLOG_TOPIC[pillar] : null
    const isNumber = isANumber(p ?? '') ? true : false
    const isPositive = isNonNegative(parseFloat(p ?? ''))
    const page: number | null =
      isNumber && isPositive ? parseFloat(p as string) || null : null
    const skip = ((page ?? 1) - 1) * BLOG_PAGE_SIZE

    let blogs: ObservableQuery.Result<QueryCombinedResult>
    try {
      // Sort by the editorial `date` field (manually curated) rather than
      // Hygraph's `createdAt` (which freezes at first insert). Without this,
      // posts created in 2024 but refreshed for 2026 still sink behind newer-
      // created but staler content. All 88 posts carry a non-null `date`, so
      // no fallback is needed.
      blogs = await getPosts(BLOG_PAGE_SIZE, skip, PostOrderByInput.DateDesc, {
        isBlog: !topic,
        locale,
        topic: topic ?? undefined,
        listOnly: true
      })
    } catch (error) {
      processHygraphRequestError(error, 'Error retrieving posts from hygraph.')
      throw error
    }

    const [firstPost, ...posts] = (blogs.data?.posts ?? []).map(withCleanTitle)
    const count = blogs.data?.postsConnection?.aggregate?.count ?? 0

    const translations = await serverSideTranslations(locale ?? 'es', [
      'common',
      'footer',
      'nav',
      'platform_stats',
      'auth',
      'blog'
    ])

    return {
      props: {
        pillar: pillar ?? null,
        ...translations,
        firstPost: (firstPost as IPost) ?? null,
        posts: posts as IPost[],
        totalPosts: count,
        page: page,
        locale,
        topic: topic ?? null
      },
      revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
    }
  }

export const getStaticProps = createGetStaticProps()
